import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, FileCheck2, BadgeCheck, ScrollText, Eye, Lock } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { parseKeyValueMap } from "@/lib/pageContent";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { pickLanguage } from "@/lib/publicLanguage";
import { usePublicCertificates, usePublicSettings } from "@/hooks/usePublicSiteData";

function getDefaultFaqs(language) {
  return pickLanguage(language, {
    en: [
      {
        q: "How do you verify supplier authenticity?",
        a: "Every supplier in our network is on-site audited for trade license, factory capacity, ISO/quality compliance, and past export track record. We verify documents through original issuing authorities before listing them as eligible.",
      },
      {
        q: "Do you provide pre-shipment inspection?",
        a: "Yes. We coordinate with SGS, Bureau Veritas, and Intertek for third-party pre-shipment inspections on request. Inspection reports are shared with you before container loading.",
      },
      {
        q: "What payment terms do you accept?",
        a: "We accept T/T (Telegraphic Transfer), L/C (Letter of Credit) at sight or D/A, and escrow via verified platforms. Terms are negotiated per shipment based on your trade history with us.",
      },
      {
        q: "Are you registered for international trade?",
        a: "Yes. We hold valid IRC (Import Registration Certificate), ERC (Export Registration Certificate), VAT registration, FBCCI membership, and ISO 9001:2015 certification. All documents are listed on this page.",
      },
    ],
    "zh-CN": [
      {
        q: "你们如何核验供应商真实性？",
        a: "我们网络中的每家供应商都会接受实地审核，包括营业执照、工厂产能、ISO/质量合规以及过往出口记录。文件在列入合格名单前，会通过原始签发机构进一步核验。",
      },
      {
        q: "你们提供装运前检验吗？",
        a: "提供。我们可根据需求协调 SGS、Bureau Veritas 和 Intertek 等第三方机构进行装运前检验，并在装柜前向您共享检验报告。",
      },
      {
        q: "你们接受哪些付款方式？",
        a: "我们接受电汇 T/T、即期信用证 L/C、承兑交单 D/A，以及通过经验证平台进行的托管支付。具体条款会根据您与我们的合作历史按每批货物协商确定。",
      },
      {
        q: "你们是否具备国际贸易注册资质？",
        a: "是的。我们持有有效的 IRC（进口注册证）、ERC（出口注册证）、VAT 注册、FBCCI 会员资格以及 ISO 9001:2015 认证，所有文件均列示在本页。",
      },
    ],
    bn: [
      {
        q: "আপনারা কীভাবে সরবরাহকারীর সত্যতা যাচাই করেন?",
        a: "আমাদের নেটওয়ার্কের প্রতিটি সরবরাহকারীকে ট্রেড লাইসেন্স, কারখানার সক্ষমতা, ISO/গুণমান-সম্মতি এবং পূর্ববর্তী রপ্তানি রেকর্ডের জন্য সাইটে গিয়ে অডিট করা হয়। যোগ্য হিসেবে তালিকাভুক্ত করার আগে আমরা নথিগুলো মূল ইস্যুকারী কর্তৃপক্ষের মাধ্যমে যাচাই করি।",
      },
      {
        q: "আপনারা কি চালান-পূর্ব পরিদর্শন দেন?",
        a: "হ্যাঁ। প্রয়োজনে আমরা SGS, Bureau Veritas এবং Intertek-এর মতো তৃতীয় পক্ষের মাধ্যমে চালান-পূর্ব পরিদর্শনের সমন্বয় করি। কনটেইনার লোডের আগে পরিদর্শন প্রতিবেদন আপনাকে শেয়ার করা হয়।",
      },
      {
        q: "আপনারা কোন পেমেন্ট টার্ম গ্রহণ করেন?",
        a: "আমরা T/T (Telegraphic Transfer), sight-এ L/C (Letter of Credit), D/A এবং যাচাইকৃত প্ল্যাটফর্মের মাধ্যমে escrow গ্রহণ করি। আপনার সঙ্গে আমাদের বাণিজ্যিক ইতিহাস অনুযায়ী প্রতিটি চালানের জন্য শর্ত নির্ধারিত হয়।",
      },
      {
        q: "আপনারা কি আন্তর্জাতিক বাণিজ্যের জন্য নিবন্ধিত?",
        a: "হ্যাঁ। আমাদের বৈধ IRC (Import Registration Certificate), ERC (Export Registration Certificate), VAT নিবন্ধন, FBCCI সদস্যপদ এবং ISO 9001:2015 সার্টিফিকেশন রয়েছে। সব নথি এই পৃষ্ঠায় দেওয়া আছে।",
      },
    ],
  });
}

function parseFaqItems(raw, fallbackFaqs) {
  if (!raw) return fallbackFaqs;
  const items = String(raw)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [question, ...answerParts] = line.split("|");
      const q = question?.trim();
      const a = answerParts.join("|").trim();
      return q && a ? { q, a } : null;
    })
    .filter(Boolean);
  return items.length ? items : fallbackFaqs;
}

const BADGE_STYLES = {
  purple: "bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)] border-[var(--rsi-purple)]/20",
  navy: "bg-[#101D38]/8 text-[var(--rsi-navy)] border-[var(--rsi-navy)]/20",
  green: "bg-[var(--rsi-green)]/10 text-[var(--rsi-green)] border-[var(--rsi-green)]/20",
};

export default function TrustCenter() {
  const [previewCert, setPreviewCert] = useState(null);
  const { language } = useSiteLanguage();
  const { data: settings } = usePublicSettings();
  const trustLabels = parseKeyValueMap(settings?.trust_page_labels || "");
  const faqs = parseFaqItems(settings?.trust_faq_items, getDefaultFaqs(language));
  const copy = {
    heroBadge: pickLanguage(language, {
      en: trustLabels.hero_badge || "Trust Center",
      "zh-CN": "信任中心",
      bn: "ট্রাস্ট সেন্টার",
    }),
    heroTitle: pickLanguage(language, {
      en: settings?.trust_hero_title || "Verified. Documented.",
      "zh-CN": "已核验，已归档。",
      bn: "যাচাইকৃত। নথিভুক্ত।",
    }),
    heroHighlight: pickLanguage(language, {
      en: settings?.trust_hero_highlight || "Audit-ready.",
      "zh-CN": "随时接受审查。",
      bn: "অডিটের জন্য প্রস্তুত।",
    }),
    heroSubtitle: pickLanguage(language, {
      en:
        settings?.trust_hero_subtitle ||
        "Our trade licenses, registration certificates, and compliance documents are listed below — every one of them verifiable with the original issuing authority.",
      "zh-CN":
        "我们的贸易执照、注册证书和合规文件均列示如下，且每一份都可以通过原始签发机构核验。",
      bn:
        "আমাদের ট্রেড লাইসেন্স, নিবন্ধন সনদ এবং কমপ্লায়েন্স নথি নিচে তালিকাভুক্ত আছে — প্রতিটি মূল ইস্যুকারী কর্তৃপক্ষের মাধ্যমে যাচাইযোগ্য।",
    }),
    verifiedLabel: pickLanguage(language, {
      en: trustLabels.verified_label || "Verified Trader",
      "zh-CN": "可信贸易伙伴",
      bn: "যাচাইকৃত বাণিজ্য অংশীদার",
    }),
    verifiedNote: pickLanguage(language, {
      en:
        settings?.trust_verified_note ||
        "R.S. International is independently registered, government-licensed, and FBCCI member — fully eligible for cross-border B2B trade.",
      "zh-CN": "R.S. International 具备独立注册、政府许可及 FBCCI 会员资格，完全符合跨境 B2B 贸易资质。",
      bn: "R.S. International স্বাধীনভাবে নিবন্ধিত, সরকার-অনুমোদিত এবং FBCCI সদস্য — সীমান্তপার B2B বাণিজ্যের জন্য সম্পূর্ণ যোগ্য।",
    }),
    libraryBadge: pickLanguage(language, {
      en: trustLabels.library_badge || "Compliance Library",
      "zh-CN": "合规资料库",
      bn: "কমপ্লায়েন্স লাইব্রেরি",
    }),
    libraryTitle: pickLanguage(language, {
      en: trustLabels.library_title || "Licenses & certifications.",
      "zh-CN": "执照与认证。",
      bn: "লাইসেন্স ও সার্টিফিকেশন।",
    }),
    compliancePrefix: pickLanguage(language, {
      en: trustLabels.compliance_prefix || "Need a verified copy for due diligence?",
      "zh-CN": "需要用于尽调的核验副本？",
      bn: "ডিউ-ডিলিজেন্সের জন্য যাচাইকৃত কপি দরকার?",
    }),
    complianceLink: pickLanguage(language, {
      en: trustLabels.compliance_link || "Email our compliance desk",
      "zh-CN": "联系合规团队邮箱",
      bn: "আমাদের কমপ্লায়েন্স ডেস্কে ইমেইল করুন",
    }),
    loading: pickLanguage(language, {
      en: "Loading…",
      "zh-CN": "加载中……",
      bn: "লোড হচ্ছে…",
    }),
    protectedPreview: pickLanguage(language, {
      en: trustLabels.preview_badge || "Protected Preview",
      "zh-CN": "受保护预览",
      bn: "সুরক্ষিত প্রিভিউ",
    }),
    verified: pickLanguage(language, {
      en: "Verified",
      "zh-CN": "已核验",
      bn: "যাচাইকৃত",
    }),
    pending: pickLanguage(language, {
      en: "Pending",
      "zh-CN": "待核验",
      bn: "অপেক্ষমাণ",
    }),
    issuer: pickLanguage(language, {
      en: "Issuer",
      "zh-CN": "签发机构",
      bn: "ইস্যুকারী",
    }),
    issued: pickLanguage(language, {
      en: "Issued",
      "zh-CN": "签发日期",
      bn: "জারি হয়েছে",
    }),
    validTill: pickLanguage(language, {
      en: "Valid till",
      "zh-CN": "有效期至",
      bn: "কার্যকর থাকবে",
    }),
    noExpiry: pickLanguage(language, {
      en: "No expiry",
      "zh-CN": "无到期日",
      bn: "মেয়াদ নেই",
    }),
    previewDocument: pickLanguage(language, {
      en: "Preview document",
      "zh-CN": "预览文件",
      bn: "নথি প্রিভিউ করুন",
    }),
    publicViewOnly: pickLanguage(language, {
      en: trustLabels.preview_only || "Public view only",
      "zh-CN": "仅供公开查看",
      bn: "শুধু পাবলিক ভিউ",
    }),
    issuerUnavailable: pickLanguage(language, {
      en: "Issuer unavailable",
      "zh-CN": "暂无签发机构信息",
      bn: "ইস্যুকারীর তথ্য নেই",
    }),
    dialogViewOnly: pickLanguage(language, {
      en: trustLabels.dialog_view_only || "View-only protected preview",
      "zh-CN": "仅查看的受保护预览",
      bn: "শুধু দেখার জন্য সুরক্ষিত প্রিভিউ",
    }),
    faqBadge: pickLanguage(language, {
      en: settings?.trust_faq_badge || "Due-Diligence FAQ",
      "zh-CN": "尽调常见问题",
      bn: "ডিউ-ডিলিজেন্স FAQ",
    }),
    faqTitle: pickLanguage(language, {
      en: settings?.trust_faq_title || "Buyer questions, answered.",
      "zh-CN": "买家关心的问题，这里都有答案。",
      bn: "ক্রেতাদের প্রশ্নের উত্তর এখানে।",
    }),
  };

  const { data: certificates = [], isLoading } = usePublicCertificates();

  return (
    <>
      {/* Hero */}
      <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="trust-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--rsi-purple)] via-[var(--rsi-navy)] to-[var(--rsi-purple)] grain" />
        <div className="container-rsi relative grid lg:grid-cols-12 gap-10 items-center text-white">
          <div className="lg:col-span-8">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
              <ShieldCheck className="h-3.5 w-3.5 text-[#9be0b3]" /> {copy.heroBadge}
            </span>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-4 leading-[1.02]">
              {copy.heroTitle}<br />
              <span className="text-[#d3bfff]">{copy.heroHighlight}</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-2xl">
              {copy.heroSubtitle}
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="glass-dark rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-white/15 grid place-items-center">
                  <BadgeCheck className="h-5 w-5 text-[#9be0b3]" />
                </div>
                <div>
                  <div className="text-xs text-white/60 uppercase tracking-wider">{copy.verifiedLabel}</div>
                  <div className="font-display font-extrabold text-lg">{settings?.trust_verified_since || "Since 1985"}</div>
                </div>
              </div>
              <div className="text-xs text-white/70 leading-relaxed">
                {copy.verifiedNote}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certificates Bento */}
      <section className="py-20 bg-[var(--rsi-bg-subtle)]" data-testid="trust-certificates">
        <div className="container-rsi">
          <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
            <div>
              <span className="badge-purple">
                <FileCheck2 className="h-3.5 w-3.5" /> {copy.libraryBadge}
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
                {copy.libraryTitle}
              </h2>
            </div>
            <div className="text-sm text-gray-500 max-w-md">
              {copy.compliancePrefix}{" "}
              <a href={`mailto:${settings?.email || "info@rsinternational.com"}`} className="text-[var(--rsi-purple)] font-semibold underline">
                {copy.complianceLink}
              </a>
              .
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-20 text-gray-500">{copy.loading}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {certificates.map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="rsi-card overflow-hidden"
                  data-testid={`certificate-${i}`}
                >
                  <div
                    className="relative aspect-[4/5] overflow-hidden bg-white select-none"
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <img
                      src={c.image_url}
                      alt={c.title}
                      className="h-full w-full object-contain pointer-events-none select-none"
                      draggable={false}
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,17,29,0.08),rgba(8,17,29,0.22))]" />
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                      <div className="rotate-[-24deg] text-center text-[10px] font-black uppercase tracking-[0.38em] text-[var(--rsi-navy)]/15">
                        RS INTERNATIONAL
                        <br />
                        PUBLIC PREVIEW ONLY
                      </div>
                    </div>
                    <div className="absolute left-3 right-3 top-3 flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/70 bg-white/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--rsi-navy)] backdrop-blur">
                        <Lock className="h-3 w-3" /> {copy.protectedPreview}
                      </span>
                      {c.country ? (
                        <span className="rounded-full border border-white/70 bg-white/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-600 backdrop-blur">
                          {c.country}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                          BADGE_STYLES[c.badge_color] || BADGE_STYLES.purple
                        }`}
                      >
                        <ShieldCheck className="h-3 w-3" /> {c.verified ? copy.verified : copy.pending}
                      </span>
                      {c.certificate_type ? (
                        <span className="inline-flex items-center rounded-full border border-gray-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                          {c.certificate_type}
                        </span>
                      ) : null}
                    </div>
                    <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
                      {c.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{copy.issuer}: {c.issuer}</p>
                    <p className="text-sm text-gray-700 mt-3 leading-relaxed line-clamp-3">
                      {c.description}
                    </p>
                    <div className="mt-4 flex items-center justify-between text-xs font-mono-plex text-gray-500">
                      <span>{copy.issued}: {c.issued_date || "—"}</span>
                      <span>{c.expiry_date ? `${copy.validTill}: ${c.expiry_date}` : copy.noExpiry}</span>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setPreviewCert(c)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--rsi-purple)] hover:gap-2 transition-all"
                        data-testid={`certificate-preview-${i}`}
                      >
                        {copy.previewDocument} <Eye className="h-3.5 w-3.5" />
                      </button>
                      <span className="text-[11px] text-gray-400">{copy.publicViewOnly}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Dialog open={Boolean(previewCert)} onOpenChange={(open) => !open && setPreviewCert(null)}>
        <DialogContent
          className="max-w-5xl bg-[#09121b] border-white/10 text-white"
          data-testid="certificate-preview-dialog"
        >
          {previewCert ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-white">
                  {previewCert.title}
                </DialogTitle>
              </DialogHeader>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-4 sm:p-5">
                <div
                  className="relative overflow-hidden rounded-2xl bg-white select-none"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <img
                    src={previewCert.image_url}
                    alt={previewCert.title}
                    className="max-h-[75vh] w-full object-contain pointer-events-none select-none"
                    draggable={false}
                  />
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="rounded-2xl border border-white/20 bg-[var(--rsi-navy)]/35 px-6 py-4 text-center backdrop-blur-sm">
                      <div className="text-xs font-black uppercase tracking-[0.35em] text-white/70">
                        RS INTERNATIONAL
                      </div>
                      <div className="mt-2 text-lg font-display font-black uppercase tracking-[0.22em] text-white/55">
                        PUBLIC VIEW ONLY
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-white/70">
                  <div>
                    {previewCert.issuer || copy.issuerUnavailable}
                    {previewCert.country ? ` · ${previewCert.country}` : ""}
                  </div>
                  <div className="text-xs uppercase tracking-[0.16em] text-white/45">
                    {copy.dialogViewOnly}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* FAQ */}
      <section className="py-24 bg-white" data-testid="trust-faq">
        <div className="container-rsi max-w-3xl">
          <span className="badge-purple">
            <ScrollText className="h-3.5 w-3.5" /> {copy.faqBadge}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            {copy.faqTitle}
          </h2>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem value={`item-${i}`} key={i} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display font-extrabold text-base text-[var(--rsi-navy)]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-sm leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
